import { FormEvent, useEffect, useState } from 'react';

export type ContributionIntent = 'suggest_decode' | 'upload_evidence' | 'request_help';

type Props = {
  open: boolean;
  intent: ContributionIntent;
  defaultCanId?: string;
  sourcePage: 'analyzer' | 'live';
  onClose: () => void;
};

type SubmitState = 'idle' | 'submitting' | 'success' | 'error';

const intentTitles: Record<ContributionIntent, string> = {
  suggest_decode: 'Suggest decode',
  upload_evidence: 'Upload evidence',
  request_help: 'Request decode help'
};

function isTextLike(file: File) {
  return file.type.startsWith('text/') || /\.(txt|log|csv|json|canlog)$/i.test(file.name);
}

async function readEvidenceFile(file: File | null) {
  if (!file) return null;
  const evidence = {
    name: file.name,
    type: file.type || 'application/octet-stream',
    size: file.size,
    text: ''
  };

  if (file.size <= 64_000 && isTextLike(file)) {
    evidence.text = await file.text();
  }

  return evidence;
}

export function DecodeContributionModal({ open, intent, defaultCanId = '', sourcePage, onClose }: Props) {
  const [canId, setCanId] = useState(defaultCanId);
  const [signalName, setSignalName] = useState('');
  const [description, setDescription] = useState('');
  const [dataPattern, setDataPattern] = useState('');
  const [vehicleMake, setVehicleMake] = useState('');
  const [vehicleModel, setVehicleModel] = useState('');
  const [vehicleYear, setVehicleYear] = useState('');
  const [evidenceNote, setEvidenceNote] = useState('');
  const [email, setEmail] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [submitState, setSubmitState] = useState<SubmitState>('idle');
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return;
    setCanId(defaultCanId);
    setSubmitState('idle');
    setError('');
  }, [defaultCanId, open]);

  async function submitContribution(event: FormEvent) {
    event.preventDefault();
    setSubmitState('submitting');
    setError('');

    try {
      const evidenceFile = await readEvidenceFile(file);
      const response = await fetch('/api/decode/contribute', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          intent,
          sourcePage,
          canId,
          signalName,
          description,
          dataPattern,
          vehicleMake,
          vehicleModel,
          vehicleYear,
          evidenceNote,
          email,
          evidenceFile
        })
      });
      const payload = await response.json() as { ok?: boolean; error?: string };
      if (!response.ok || !payload.ok) throw new Error(payload.error || 'Contribution submission failed.');
      setSubmitState('success');
    } catch (submissionError) {
      setSubmitState('error');
      setError(submissionError instanceof Error ? submissionError.message : 'Contribution submission failed.');
    }
  }

  function resetAndClose() {
    if (submitState === 'submitting') return;
    setSignalName('');
    setDescription('');
    setDataPattern('');
    setVehicleMake('');
    setVehicleModel('');
    setVehicleYear('');
    setEvidenceNote('');
    setEmail('');
    setFile(null);
    onClose();
  }

  if (!open) return null;

  return (
    <div className="modal-backdrop" role="presentation" onMouseDown={(event) => {
      if (event.target === event.currentTarget) resetAndClose();
    }}>
      <section className="contribution-modal" role="dialog" aria-modal="true" aria-labelledby="contribution-title">
        <div className="panel-heading">
          <div>
            <p className="eyebrow">Decode contribution</p>
            <h2 id="contribution-title">{intentTitles[intent]}</h2>
            <p className="panel-note">This is an early contribution system. Signals are reviewed before being added.</p>
          </div>
          <button className="button ghost small" type="button" onClick={resetAndClose}>Close</button>
        </div>

        {submitState === 'success' ? (
          <div className="contribution-success">
            <h3>Contribution submitted</h3>
            <p className="success-text">Your contribution is now part of the SmartCAN community dataset (pending review).</p>
            <p className="panel-note">Your submission is marked pending review. No public decode definition is changed automatically.</p>
            <a className="button secondary" href="/decode">View contributions</a>
            <button className="button primary" type="button" onClick={resetAndClose}>Done</button>
          </div>
        ) : (
          <form className="contribution-form" onSubmit={submitContribution}>
            <label>
              CAN ID
              <input value={canId} onChange={(event) => setCanId(event.target.value)} placeholder="0x025" required />
            </label>
            <label>
              Signal name
              <input value={signalName} onChange={(event) => setSignalName(event.target.value)} placeholder="Wheel speed front left" required maxLength={120} />
            </label>
            <label>
              Description
              <textarea value={description} onChange={(event) => setDescription(event.target.value)} placeholder="What does this signal represent and how did you verify it?" maxLength={1200} />
            </label>
            <label>
              Data pattern
              <input value={dataPattern} onChange={(event) => setDataPattern(event.target.value)} placeholder="Example: byte 2 increases with speed, A8 00 pattern, bit 3 toggles" maxLength={240} />
            </label>
            <label>
              Vehicle make
              <input value={vehicleMake} onChange={(event) => setVehicleMake(event.target.value)} placeholder="Toyota, Volvo, custom robot..." maxLength={120} />
            </label>
            <label>
              Vehicle model
              <input value={vehicleModel} onChange={(event) => setVehicleModel(event.target.value)} placeholder="Sienna, industrial node, drive base..." maxLength={120} />
            </label>
            <label>
              Vehicle year
              <input value={vehicleYear} onChange={(event) => setVehicleYear(event.target.value)} placeholder="2021" inputMode="numeric" maxLength={4} />
            </label>
            <label>
              Evidence note
              <textarea value={evidenceNote} onChange={(event) => setEvidenceNote(event.target.value)} placeholder="Add notes, test conditions, links, or sample observations." maxLength={1600} />
            </label>
            <label>
              Evidence file optional
              <input type="file" accept=".txt,.log,.csv,.json,.canlog,.bin,application/octet-stream,text/plain" onChange={(event) => setFile(event.currentTarget.files?.[0] ?? null)} />
            </label>
            {file && (
              <p className="panel-note">
                Attached: {file.name} ({file.size.toLocaleString()} bytes). Text-like files up to 64 KB include content; larger or binary files submit metadata only.
              </p>
            )}
            <label>
              Email optional
              <input type="email" value={email} onChange={(event) => setEmail(event.target.value)} placeholder="you@example.com" />
            </label>
            {error && <p className="error-text">{error}</p>}
            <div className="contribution-actions">
              <button className="button primary" type="submit" disabled={submitState === 'submitting'}>
                {submitState === 'submitting' ? 'Submitting...' : 'Submit contribution'}
              </button>
              <button className="button ghost" type="button" disabled={submitState === 'submitting'} onClick={resetAndClose}>Cancel</button>
            </div>
          </form>
        )}
      </section>
    </div>
  );
}
